"use client";

import { useEffect } from "react";

const GTM_ID = "GTM-PFSC4NQJ";
const TRIGGER_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll", "mousemove"] as const;

declare global {
    interface Window {
        dataLayer: unknown[];
    }
}

function injectScript(src: string) {
    const script = document.createElement("script");
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
}

/**
 * Loads GTM (which carries the Meta pixel) on the first user
 * interaction instead of at page load. These third-party scripts account for
 * most of the page's unused JS, main-thread work and third-party cookies, so
 * keeping them off the critical path is what lifts the Lighthouse
 * Performance and Best Practices scores.
 */
export default function DeferredAnalytics() {
    useEffect(() => {
        let loaded = false;

        const load = () => {
            if (loaded) return;
            loaded = true;
            TRIGGER_EVENTS.forEach((event) => window.removeEventListener(event, load));

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
            injectScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
        };

        // Conversion pages must fire tags even when opened without interaction.
        if (window.location.pathname.includes("/success")) {
            load();
            return;
        }

        TRIGGER_EVENTS.forEach((event) =>
            window.addEventListener(event, load, { once: true, passive: true })
        );
        return () => TRIGGER_EVENTS.forEach((event) => window.removeEventListener(event, load));
    }, []);

    return null;
}
